import type { TranslationKey } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import type { PawnData } from '../../state/entities';
import { t } from '../../state/i18n';
import {
  EquipmentSlot,
  type EquipmentSlotType,
  type ItemData,
  selectedPawnEquipment,
} from '../../state/pawnGear';

const equipmentPanel = tv({
  slots: {
    root: 'flex flex-col',
    title: [
      'text-center text-rs-gold font-semibold text-[0.8em] mb-[0.85em] tracking-wide',
      'border-b border-rs-stone-light/30 pb-[0.55em]',
    ],
    section: 'mb-[0.8em]',
    sectionLabel: ['text-[0.62em] uppercase tracking-wider text-rs-parchment-dark mb-[0.4em]'],
    slotRow: 'flex gap-[0.4em] mb-[0.4em]',
    armorSection: ['mt-[0.55em] pt-[0.55em]', 'border-t border-rs-stone-light/20'],
    statsSection: ['mt-[0.8em] pt-[0.55em]', 'border-t border-rs-stone-light/30'],
    statsRow: 'flex justify-between text-[0.68em] mb-[0.28em]',
    statsLabel: 'text-rs-parchment-dark',
    statsValue: 'text-rs-parchment',
  },
});

const equipSlot = tv({
  base: [
    'flex-1',
    'h-[3em]',
    'bg-gradient-to-b from-rs-stone-dark/80 to-rs-stone-dark',
    'border border-rs-stone-light/40',
    'rounded-[0.3em]',
    'flex items-center gap-[0.55em] px-[0.55em]',
    'transition-all duration-150',
    'cursor-pointer',
    'hover:border-rs-gold/40',
    'group',
  ],
  variants: {
    hasItem: {
      true: 'border-rs-stone-light/60',
      false: '',
    },
    slotType: {
      primary: 'bg-gradient-to-b from-red-900/20 to-rs-stone-dark',
      headgear: '',
      torso: '',
      legs: '',
      utility: 'bg-gradient-to-b from-amber-900/20 to-rs-stone-dark',
    },
  },
});

const slotIcon = tv({
  base: [
    'w-[1.9em] h-[1.9em]',
    'bg-rs-stone-dark/60',
    'border border-rs-stone-light/30',
    'rounded-[0.28em]',
    'flex items-center justify-center',
    'text-rs-stone-light/50 text-[0.68em]',
  ],
  variants: {
    hasItem: {
      true: 'border-rs-gold/30 text-rs-gold',
      false: '',
    },
  },
});

const slotInfo = tv({
  base: 'flex-1 min-w-0',
});

const slotLabel = tv({
  base: 'text-[0.62em] text-rs-parchment-dark uppercase tracking-wide',
});

const slotValue = tv({
  base: 'text-[0.75em] text-rs-parchment truncate',
  variants: {
    empty: {
      true: 'text-rs-stone-light/50 italic',
      false: '',
    },
  },
});

// Slot configuration
const SLOT_CONFIG: Record<
  EquipmentSlotType,
  { labelKey: TranslationKey; icon: string; emptyKey: TranslationKey }
> = {
  [EquipmentSlot.PRIMARY]: { labelKey: 'ui.slots.weapon', icon: 'W', emptyKey: 'ui.slots.unarmed' },
  [EquipmentSlot.HEADGEAR]: { labelKey: 'ui.slots.head', icon: 'H', emptyKey: 'ui.slots.none' },
  [EquipmentSlot.TORSO]: { labelKey: 'ui.slots.torso', icon: 'T', emptyKey: 'ui.slots.none' },
  [EquipmentSlot.LEGS]: { labelKey: 'ui.slots.legs', icon: 'L', emptyKey: 'ui.slots.none' },
  [EquipmentSlot.UTILITY]: {
    labelKey: 'ui.slots.utilitySlot',
    icon: 'U',
    emptyKey: 'ui.slots.none',
  },
};

interface EquipSlotProps {
  slot: EquipmentSlotType;
  item: ItemData | null;
}

const EquipSlot = ({ slot, item }: EquipSlotProps) => {
  const hasItem = item !== null;
  const config = SLOT_CONFIG[slot];
  const hasIcon = hasItem && item.icon && item.icon.length > 0;

  return (
    <div class={equipSlot({ hasItem, slotType: slot })}>
      <div class={slotIcon({ hasItem })}>
        {hasItem ? (
          hasIcon ? (
            <img
              src={item.icon}
              alt={item.name}
              class="w-[1.6em] h-[1.6em] object-contain [image-rendering:pixelated]"
              draggable={false}
            />
          ) : (
            item.name.charAt(0).toUpperCase()
          )
        ) : (
          config.icon
        )}
      </div>
      <div class={slotInfo()}>
        <div class={slotLabel()}>{t(config.labelKey)}</div>
        <div class={slotValue({ empty: !hasItem })}>{hasItem ? item.name : t(config.emptyKey)}</div>
      </div>
    </div>
  );
};

interface EquipmentPanelProps {
  pawn: PawnData;
}

export const EquipmentPanel = ({ pawn: _pawn }: EquipmentPanelProps) => {
  const equipment = selectedPawnEquipment.value;
  const styles = equipmentPanel();

  if (!equipment) {
    return (
      <div class={styles.root()}>
        <div class={styles.title()}>{t('ui.panels.gear')}</div>
        <div class="text-rs-parchment-dark text-[0.72em] text-center py-[2.2em]">
          {t('ui.empty.noPawnSelected')}
        </div>
      </div>
    );
  }

  return (
    <div class={styles.root()}>
      <div class={styles.title()}>{t('ui.panels.gear')}</div>

      {/* Weapon */}
      <div class={styles.section()}>
        <div class={styles.sectionLabel()}>{t('ui.labels.equipment')}</div>
        <EquipSlot slot={EquipmentSlot.PRIMARY} item={equipment[EquipmentSlot.PRIMARY]} />
      </div>

      {/* TODO: Apparel slots hidden until armor items exist */}
      {/* TODO: Utility slot hidden until utility items exist */}
      {/* TODO: Armor stats hidden until armor items exist */}
    </div>
  );
};
